import type { useGetDirectoryWithShared } from '@repo/api/file-system/useGetDirectoryWithShared';
import type { DirectoryPublic } from '@repo/types/directory';
import type { FilePublic, FilePublicWithPermissions } from '@repo/types/files';

import { getBase64 } from '@repo/lib/fs/getBase64';
import { FileIcon, Info } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';

import type { useSelectFsItems } from '@/hooks/fs';

import { DirectoryListEmpty, DirectoryListPending } from '@/components/features/files/DirectoryList';
import FSPath from '@/components/features/files/FsPath';
import FsSearchCommand from '@/components/features/files/select-fs-items/FsSearchCommand';
import SelectableItemList from '@/components/features/files/select-fs-items/SelectableItemsList';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { FolderIcon } from '@/components/ui/file-icon';
import { List } from '@/components/ui/list';
import { Tag } from '@/components/ui/tags';

interface FsItemSelectionProps {
  fsType: string;
  state: ReturnType<typeof useSelectFsItems>['state'];
  actions: ReturnType<typeof useSelectFsItems>['actions'];
  queryData: ReturnType<typeof useGetDirectoryWithShared>;
  handleNavigateTo: (directoryId?: string) => void;
  multiple?: boolean;
  onDoubleClickSelection?: (item: DirectoryPublic | FilePublicWithPermissions) => Promise<void>;
}

export default function FsItemSelection(fsItemSelectionProps: FsItemSelectionProps) {
  const { state, actions, queryData, handleNavigateTo, fsType, multiple, onDoubleClickSelection } = fsItemSelectionProps;
  const isEmpty: boolean = queryData.isSuccess && queryData.fsItems.files.length === 0 && queryData.fsItems.dirs.length === 0;

  const handleFileSelect = (file: FilePublic) => {
    actions.handleClickItem(file);
  };

  const truncateName = (name: string): string => {
    const nameLength: number = name.length;
    const sliceLength: number = 35;
    return nameLength > sliceLength ? name.slice(0, sliceLength) + '...' : name;
  };

  const getThumbnail = (item: FilePublic) => {
    if (!item.thumbnail) return;
    return getBase64({
      fileType: item.fileType,
      base64: item.thumbnail,
      clear: true,
    });
  };

  return (
    <div className="space-y-5">
      <div className={'space-y-3'}>
        <FsSearchCommand
          onDirectorySelect={(directory) => handleNavigateTo(directory.id)}
          onFileSelect={handleFileSelect}
          isFileSelected={actions.handleIsItemSelected}
          placeholder="Wpisz nazwę pliku/folderu aby wyszukać..."
        />
        <FSPath
          directory={queryData.directoryData}
          isLoading={queryData.isLoading}
          isError={queryData.isError}
          onRouteChange={handleNavigateTo}
          rootPath={fsType === 'SHARED_DRIVE' ? { id: uuidv4(), name: 'Udostępnione', parentDirId: null } : undefined}
        />
      </div>
      <div className="flex min-h-7 flex-wrap gap-1">
        {state.selected?.directories.map((dir) => {
          return (
            <Tag key={dir.id} variant={'muted'} onRemove={() => actions.handleRemoveSelected(dir)}>
              <FolderIcon className="size-3" />
              <span>{truncateName(dir.name)}</span>
            </Tag>
          );
        })}
        {state.selected?.files.map((file) => {
          return (
            <Tag key={file.id} variant={'muted'} onRemove={() => actions.handleRemoveSelected(file)}>
              <Avatar size={'sm'} className="-ms-2.5 size-6 rounded-sm">
                <AvatarImage src={getThumbnail(file)} alt={file.name} />
                <AvatarFallback>
                  <FileIcon />
                </AvatarFallback>
              </Avatar>

              <span>{truncateName(file.name)}</span>
            </Tag>
          );
        })}
      </div>
      {queryData.isError && (
        <Alert variant={'destructive'}>
          <Info />
          <AlertDescription>{queryData.errorMessage.detail}</AlertDescription>
        </Alert>
      )}
      <List className={queryData.isError ? 'hidden' : ''}>
        {isEmpty && <DirectoryListEmpty />}
        {queryData.isPending && <DirectoryListPending />}

        {queryData.isSuccess && (
          <SelectableItemList
            items={state.items}
            isItemSelected={actions.handleIsItemSelected}
            isItemSelectable={actions.handleCanSelectItem}
            onClickSelection={actions.handleClickItem}
            onNavigate={handleNavigateTo}
            multiple={multiple}
            onDoubleClickSelection={onDoubleClickSelection}
          />
        )}
      </List>
    </div>
  );
}
